<script setup>
/**
 * FormControlTemplatesTemplatesFilterAttribute
 * A conditional dropdown for selecting a filter attribute based on the selected component.
 * This control is only visible when the 'contentType' is 'Filter' and a 'componentId' is present.
 * It dynamically populates with supported attributes from the selected component's structure.
 */
import {computed, watch} from 'vue'
import {useComponentStructureStore} from '@/stores/componentStructure'
import FormControlPlainText from "./FormControlPlainText.vue"

const props = defineProps({
    formId: String,
    field: Object,
    currentData: Object,
})

const componentStructureStore = useComponentStructureStore()

const componentId = computed(() => props.currentData.componentId)

const show = computed(() => {
    return props.currentData.contentType === 'Filter' && componentId.value
})

const options = computed(() => {
    if (componentId.value) {
        const opts = []
        const componentStructure = componentStructureStore.get(componentId.value)

        if (componentStructure) {
            componentStructure.forEach(obj => {
                if (
                    obj.type !== 'fieldset' &&
                    ![
                        'validFrom',
                        'validTo',
                        'createdBy',
                        'modifiedBy',
                        'status'
                    ].includes(obj.columnName) &&
                    ['date', 'datetime-local'].includes(obj.type)
                ) {
                    opts.push({
                        value: obj.columnName,
                        text: obj.title
                    })
                }
            })
        }
        return opts
    }
    return []
})

watch(show, (isVisible) => {
    if (isVisible) {
        props.field.visibility = ''
    } else {
        props.field.visibility = 'hidden'
        props.currentData.filterAttribute = ''
    }
}, {immediate: true})

watch(componentId, (newValue, oldValue) => {
    if (newValue !== oldValue) {
        // Unset the selected value whenever the component changes
        props.currentData.filterAttribute = ''
    }
})
</script>

<template>
    <div v-if="show">
        <select v-if="options && options.length" :id="field.id" v-model="currentData[field.name]" :name="field.name"
                class="form-select" required>
            <option :value="''">Select…</option>
            <option v-for="option in options" :key="option.value" :value="option.value">
                {{ option.text }}
            </option>
        </select>

        <FormControlPlainText v-else :form-id="formId">
            No supported attributes are available.
        </FormControlPlainText>
    </div>
</template>